import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { applySEO } from '@/components/feature/SEOHead';
import { categories, hotNovels } from '@/mocks/novelData';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryName = currentCategory ? currentCategory.name : '全部';

  const filteredNovels = activeCategory === 'all'
    ? hotNovels
    : hotNovels.filter((n) => {
        const cat = categories.find((c) => c.slug === activeCategory);
        return cat && n.category === cat.name;
      });

  useEffect(() => {
    applySEO({
      title: `${categoryName}小说 - 免费在线阅读 | 笔趣阁`,
      description: `笔趣阁${categoryName}小说频道，海量${categoryName}小说免费在线阅读，最新章节同步更新，全文无删减。涵盖热门${categoryName}作品，支持手机电脑多端阅读。`,
      keywords: `${categoryName}小说,${categoryName}笔趣阁,${categoryName}免费阅读,${categoryName}最新章节,笔趣阁${categoryName}`,
      canonicalUrl: typeof window !== 'undefined' ? window.location.origin + ((window as any).__BASE_PATH__ || '') + '/category/' + activeCategory : '',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${categoryName}小说 - 笔趣阁`,
        description: `笔趣阁${categoryName}小说频道，海量${categoryName}小说免费在线阅读，最新章节同步更新。`,
        url: typeof window !== 'undefined' ? window.location.origin + ((window as any).__BASE_PATH__ || '') + '/category/' + activeCategory : '',
        isPartOf: {
          '@type': 'WebSite',
          name: '笔趣阁',
        },
      },
    });
  }, [activeCategory, categoryName]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览' },
                { label: categoryName },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-3">
              {categoryName}小说
            </h1>
            <p className="text-foreground-500 text-sm mt-1">
              共 {filteredNovels.length} 部作品 · 支持手机电脑多端阅读
            </p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex gap-1 overflow-x-auto py-3 flex-wrap">
              <Link
                to="/category/all"
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  activeCategory === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Novel List */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            {filteredNovels.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {filteredNovels.map((novel) => (
                  <Link
                    key={novel.id}
                    to={`/article/${novel.latestChapterId}`}
                    className="novel-card cursor-pointer group text-center"
                  >
                    <div className="w-full aspect-[3/4] rounded-md overflow-hidden mb-3">
                      <img
                        src={novel.coverImage}
                        alt={novel.title}
                        title={`${novel.title} - ${novel.author}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm group-hover:text-primary-600 transition-colors truncate">
                      {novel.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{novel.author}</p>
                    <div className="flex items-center justify-center gap-2 mt-1.5 flex-wrap">
                      <span className="px-2 py-0.5 text-xs bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                        {novel.category}
                      </span>
                      <span className="text-xs text-foreground-400">{novel.status}</span>
                    </div>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <span className="text-xs text-foreground-600">{novel.rating}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                  <i className="ri-book-2-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无作品，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}